import { useEffect, useState } from "react";
import { brandAssetCandidates, getBrandSlug } from "@/lib/brand";
import type { BrandAssetKind } from "@/lib/brand";

const probed = new Map<string, Promise<string | null>>();

function isImageResponse(response: Response) {
  // The SPA fallback (dev server / self-host / Vercel rewrite) answers any unknown path with
  // index.html and a 200 status, so `ok` alone can't tell a real asset from a missing one.
  return response.ok && (response.headers.get("content-type") ?? "").startsWith("image/");
}

function probe(url: string): Promise<string | null> {
  let pending = probed.get(url);
  if (!pending) {
    pending = fetch(url, { method: "HEAD" }).then((response) => (isImageResponse(response) ? url : null)).catch(() => null);
    probed.set(url, pending);
  }
  return pending;
}

/**
 * Resolves the first existing per-domain brand asset (`/brand/<slug>/<kind>.{svg,png}`) for the
 * current hostname, or `null` while unresolved and whenever the domain has none - callers keep
 * their current fallback visual (text mark, static favicon, no badge) in that case.
 */
export function useBrandAsset(kind: BrandAssetKind): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const candidates = brandAssetCandidates(kind, getBrandSlug());
    if (candidates.length === 0) return;
    void Promise.all(candidates.map(probe)).then((results) => {
      if (active) setUrl(results.find((value): value is string => Boolean(value)) ?? null);
    });
    return () => {
      active = false;
    };
  }, [kind]);

  return url;
}
